// Multi-step SRI delivery status track. Polls /sri-status every 5s while in
// transit; renders Submit / Download RIDE / Send by Email actions per state.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';

export type SriDocType =
  | 'invoices'
  | 'credit-memos'
  | 'withholding-receipts'
  | 'nota-debito'
  | 'liquidacion-compra'
  | 'guia-remision';

interface SriStatus {
  sri_status: string | null;
  tipo_emision: '1' | '2';
  numero_autorizacion: string | null;
  sri_authorized_at: string | null;
  sri_messages: Array<{ identificador: string; mensaje: string; tipo: string }> | null;
  sri_emailed_at: string | null;
}

interface Props {
  docType: SriDocType;
  docId: string;
}

function basePath(docType: SriDocType, docId: string): string {
  if (docType === 'withholding-receipts') {
    return `/bookkeeping/sri/withholding-receipts/${docId}`;
  }
  if (docType === 'liquidacion-compra') {
    return `/bookkeeping/purchases/liquidacion-compra/${docId}`;
  }
  return `/bookkeeping/sales/${docType}/${docId}`;
}

export function SriStatusTrack({ docType, docId }: Props) {
  const { t } = useTranslation('bookkeeping');
  const qc = useQueryClient();
  const base = basePath(docType, docId);

  const statusQ = useQuery({
    queryKey: ['sri-status', docType, docId],
    queryFn: () => api<SriStatus>(`${base}/sri-status`),
    refetchInterval: (q) => {
      const data = q.state.data as SriStatus | undefined;
      if (!data) return 5000;
      if (
        data.sri_status === 'RECIBIDA' ||
        data.sri_status === 'EN_PROCESO' ||
        data.sri_status === 'CONTINGENCIA_PENDING'
      )
        return 5000;
      return false;
    },
  });

  const submitMut = useMutation({
    mutationFn: () => api(`${base}/submit`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sri-status', docType, docId] }),
  });

  const emailMut = useMutation({
    mutationFn: () => api(`${base}/email`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sri-status', docType, docId] }),
  });

  const status = statusQ.data?.sri_status ?? null;
  const isAutorizado = status === 'AUTORIZADO';
  const isInTransit =
    status === 'RECIBIDA' || status === 'EN_PROCESO' || status === 'CONTINGENCIA_PENDING';
  const isError = status === 'NO_AUTORIZADO' || status === 'DEVUELTA' || status === 'RECHAZADA';

  return (
    <div className="rounded border bg-white p-3 text-sm">
      <h2 className="mb-3 font-semibold">{t('sri.title')}</h2>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Step label={t('sri.step.signed')} done />
        <Arrow />
        <Step
          label={t('sri.step.recepcion')}
          done={status === 'RECIBIDA' || isAutorizado}
          active={!status || status === 'PENDING'}
        />
        <Arrow />
        <Step
          label={t('sri.step.autorizado')}
          done={isAutorizado}
          active={isInTransit}
          error={isError}
        />
        <Arrow />
        <Step
          label={t('sri.step.email_sent')}
          done={!!statusQ.data?.sri_emailed_at}
          active={isAutorizado && !statusQ.data?.sri_emailed_at}
        />
      </div>

      {isError && statusQ.data?.sri_messages && (
        <ul className="mt-2 list-disc pl-5 text-xs text-red-700">
          {statusQ.data.sri_messages.map((m) => (
            <li key={m.identificador}>
              [{m.identificador}] {m.mensaje}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {!isAutorizado && !isInTransit && (
          <button
            type="button"
            onClick={() => submitMut.mutate()}
            disabled={submitMut.isPending}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
          >
            {t('sri.actions.submit')}
          </button>
        )}
        {isAutorizado && (
          <>
            <a
              href={`${import.meta.env.VITE_API_URL ?? '/api'}${base}/ride`}
              className="rounded border px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50"
            >
              {t('sri.actions.download_ride')}
            </a>
            <button
              type="button"
              onClick={() => emailMut.mutate()}
              disabled={emailMut.isPending}
              className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:bg-slate-300"
            >
              {t('sri.actions.send_email')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Step({
  label,
  done,
  active,
  error,
}: {
  label: string;
  done?: boolean;
  active?: boolean;
  error?: boolean;
}) {
  const cls = error
    ? 'bg-red-100 text-red-800'
    : done
      ? 'bg-emerald-100 text-emerald-800'
      : active
        ? 'bg-blue-100 text-blue-800'
        : 'bg-slate-100 text-slate-500';
  const icon = error ? '✗' : done ? '✓' : active ? '⏳' : '○';
  return (
    <span className={`rounded px-2 py-0.5 ${cls}`}>
      {icon} {label}
    </span>
  );
}

function Arrow() {
  return <span className="text-slate-400">→</span>;
}
